import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setMobileCatOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background-50 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <a
          href="/"
          className="flex items-center gap-2 cursor-pointer flex-shrink-0"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
        >
          <div className="w-8 h-8 flex items-center justify-center">
            <i
              className={`ri-grid-line text-xl ${
                isScrolled ? "text-foreground-900" : "text-background-50"
              }`}
            ></i>
          </div>
          <span
            className={`font-heading text-lg font-semibold whitespace-nowrap transition-colors duration-300 ${
              isScrolled ? "text-foreground-900" : "text-background-50"
            }`}
          >
            Kakobuy Spreadsheet
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-5">
          <a
            href="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isScrolled
                ? "text-foreground-800 hover:text-foreground-950"
                : "text-background-50/90 hover:text-background-50"
            }`}
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            Strona główna
          </a>
          <a
            href="/poradnik-zakupowy"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isScrolled
                ? "text-foreground-800 hover:text-foreground-950"
                : "text-background-50/90 hover:text-background-50"
            }`}
            onClick={(e) => {
              e.preventDefault();
              navigate("/poradnik-zakupowy");
            }}
          >
            Poradnik zakupowy
          </a>
          <a
            href="/artykul/co-to-jest-kakobuy-spreadsheet"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              isScrolled
                ? "text-foreground-800 hover:text-foreground-950"
                : "text-background-50/90 hover:text-background-50"
            }`}
            onClick={(e) => {
              e.preventDefault();
              navigate("/artykul/co-to-jest-kakobuy-spreadsheet");
            }}
          >
            Co to jest?
          </a>
          <div className="relative">
            <select
              className={`appearance-none rounded-lg px-4 py-2 pr-10 text-sm font-medium cursor-pointer transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent-400 ${
                isScrolled
                  ? "bg-background-100 border border-background-300 text-foreground-800 hover:border-background-400"
                  : "bg-background-50/15 backdrop-blur-sm border border-background-50/20 text-background-50 hover:bg-background-50/25"
              }`}
              onChange={(e) => {
                const url = e.target.value;
                if (url) {
                  window.open(url, "_blank", "noopener,nofollow");
                  e.target.value = "";
                }
              }}
              defaultValue=""
            >
              <option value="" disabled className="text-foreground-800">
                Kategorie produktów
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.url} className="text-foreground-800">
                  {cat.namePl} ({cat.nameEn})
                </option>
              ))}
            </select>
            <i
              className={`ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm ${
                isScrolled ? "text-foreground-500" : "text-background-50/70"
              }`}
            ></i>
          </div>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              isScrolled
                ? "bg-foreground-900 text-background-50 hover:bg-foreground-800"
                : "bg-background-50 text-foreground-900 hover:bg-background-100"
            }`}
          >
            Kakobuy Spreadsheet
          </a>
        </nav>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative"
          onClick={() => setIsOpen(!isOpen)}
        >
          <i
            className={`text-xl transition-colors duration-300 ${
              isScrolled ? "text-foreground-900" : "text-background-50"
            } ${isOpen ? "ri-close-line" : "ri-menu-line"}`}
          ></i>
        </button>
      </div>

      {isOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="p-4 flex flex-col gap-2">
            <a
              href="/"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                setIsOpen(false);
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-home-4-line text-lg text-foreground-700"></i>
              </div>
              Strona główna
            </a>
            <a
              href="/poradnik-zakupowy"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate("/poradnik-zakupowy");
                setIsOpen(false);
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-book-open-line text-lg text-foreground-700"></i>
              </div>
              Poradnik zakupowy
            </a>
            <a
              href="/artykul/co-to-jest-kakobuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate("/artykul/co-to-jest-kakobuy-spreadsheet");
                setIsOpen(false);
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-question-line text-lg text-foreground-700"></i>
              </div>
              Co to jest Kakobuy?
            </a>

            <button
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer w-full text-left"
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-grid-line text-lg text-foreground-700"></i>
              </div>
              <span className="flex-1">Kategorie produktów</span>
              <i
                className={`ri-arrow-down-s-line transition-transform duration-200 ${
                  mobileCatOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-11 flex flex-col gap-1 border-l-2 border-background-200 ml-3">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => setIsOpen(false)}
                  >
                    {cat.namePl}
                    <span className="text-foreground-400 text-xs ml-1">({cat.nameEn})</span>
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 my-2"></div>

            <div className="px-3">
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2 block">
                Artykuły SEO
              </span>
            </div>
            <a
              href="/artykul/kakobuy-spreadsheet-polska-2026"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate("/artykul/kakobuy-spreadsheet-polska-2026");
                setIsOpen(false);
              }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></div>
              Kakobuy Spreadsheet Polska 2026
            </a>
            <a
              href="/artykul/jak-korzystac-z-kakobuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate("/artykul/jak-korzystac-z-kakobuy-spreadsheet");
                setIsOpen(false);
              }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></div>
              Jak korzystać z arkusza
            </a>
            <a
              href="/artykul/czy-kakobuy-jest-bezpieczny"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate("/artykul/czy-kakobuy-jest-bezpieczny");
                setIsOpen(false);
              }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-secondary-500 flex-shrink-0"></div>
              Czy Kakobuy jest bezpieczny
            </a>

            <div className="border-t border-background-200 my-2"></div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="bg-foreground-900 text-background-50 px-5 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer text-center"
              onClick={() => setIsOpen(false)}
            >
              Kakobuy Spreadsheet →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}